import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const DownloadsContext = createContext();

export const useDownloads = () => {
  const context = useContext(DownloadsContext);
  if (!context) {
    throw new Error('useDownloads must be used within a DownloadsProvider');
  }
  return context;
};

// Global reference to downloads context methods (for non-React code)
let downloadsContextRef = null;

export const DownloadsProvider = ({ children }) => {
  const [downloads, setDownloads] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Load downloads from localStorage on mount
  useEffect(() => {
    const savedDownloads = localStorage.getItem('downloads');
    if (savedDownloads) {
      try {
        const parsed = JSON.parse(savedDownloads);
        // Don't restore blob data from localStorage (it's too large)
        const cleaned = parsed.map(d => ({ ...d, blob: null }));
        setDownloads(cleaned);
      } catch (error) {
        console.error('Error loading downloads from localStorage:', error);
      }
    }
  }, []);

  // Save downloads to localStorage whenever downloads change (without blob)
  useEffect(() => {
    if (downloads.length > 0) {
      // Don't save blob to localStorage (it's too large)
      const downloadsWithoutBlob = downloads.map(d => ({ ...d, blob: null }));
      localStorage.setItem('downloads', JSON.stringify(downloadsWithoutBlob));
    } else {
      localStorage.removeItem('downloads');
    }
  }, [downloads]);

  const addDownload = useCallback((download) => {
    const newDownload = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      fileName: download.fileName,
      fileIds: download.fileIds,
      status: 'creating', // 'creating', 'downloading', 'completed', 'error'
      progress: 0,
      createdAt: new Date().toISOString(),
      downloadUrl: null, // Will be set when download completes
      blob: null, // Will store the blob when download completes
      cancelTokenSource: download.cancelTokenSource || null, // Store cancel token for cancellation
      ...download
    };

    setDownloads((prev) => [newDownload, ...prev]);
    setIsPopupOpen(true); // Auto-open popup when new download starts
    return newDownload.id;
  }, []);

  const updateDownload = useCallback((id, updates) => {
    setDownloads((prev) =>
      prev.map((download) =>
        download.id === id ? { ...download, ...updates } : download
      )
    );
  }, []);

  const removeDownload = useCallback((id) => {
    setDownloads((prev) => {
      const updated = prev.filter((download) => download.id !== id);
      return updated;
    });
  }, []);

  const clearCompletedDownloads = useCallback(() => {
    setDownloads((prev) => prev.filter((download) => download.status !== 'completed'));
  }, []);

  const clearAllDownloads = useCallback(() => {
    // Cancel any active downloads before clearing
    setDownloads((prev) => {
      prev.forEach((download) => {
        if (download.cancelTokenSource && (download.status === 'creating' || download.status === 'downloading')) {
          download.cancelTokenSource.cancel('All downloads cleared');
        }
      });
      return [];
    });
  }, []);

  const togglePopup = useCallback(() => {
    setIsPopupOpen((prev) => !prev);
  }, []);

  const openPopup = useCallback(() => {
    setIsPopupOpen(true);
  }, []);

  const closePopup = useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  // Expose context methods globally for fileService
  useEffect(() => {
    downloadsContextRef = {
      addDownload,
      updateDownload,
      removeDownload,
    };
    return () => {
      downloadsContextRef = null;
    };
  }, [addDownload, updateDownload, removeDownload]);

  return (
    <DownloadsContext.Provider
      value={{
        downloads,
        isPopupOpen,
        addDownload,
        updateDownload,
        removeDownload,
        clearCompletedDownloads,
        clearAllDownloads,
        togglePopup,
        openPopup,
        closePopup,
      }}
    >
      {children}
    </DownloadsContext.Provider>
  );
};

// Export function to access downloads context from non-React code
export const getDownloadsContext = () => downloadsContextRef;
